import { useCallback, useEffect, useState } from 'react';
import { UserCard, User } from '../types';
import { cards as cardsApi, giveaways as giveawaysApi } from '../api';
import SlabCard from '../components/SlabCard';
import RarityBadge from '../components/RarityBadge';
import ListModal from '../components/ListModal';

type SortOption = 'newest' | 'rarest' | 'value';

interface CollectionProps {
  user: User;
}

interface ActionSheetProps {
  card: UserCard;
  onClose: () => void;
  onListForSale: () => void;
  onCreateGiveaway: () => void;
}

function ActionSheet({ card, onClose, onListForSale, onCreateGiveaway }: ActionSheetProps) {
  const isListed = Boolean(card.listed);
  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div
        className="w-full bg-surface-card rounded-t-2xl p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />

        <div className="flex items-center gap-3 pb-2 border-b border-white/5">
          <div className="w-12">
            <SlabCard card={card} size="sm" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold">{card.pokemon_name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <RarityBadge rarity={card.rarity} />
              <span className="text-accent-gold text-sm">${card.market_price_usd.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {isListed ? (
          <p className="text-center text-gray-400 text-sm py-2">
            This card is currently listed for sale
          </p>
        ) : (
          <>
            <button
              onClick={onListForSale}
              className="w-full py-3 rounded-xl bg-accent-purple text-white font-semibold text-sm"
            >
              💎 List for Sale
            </button>
            <button
              onClick={onCreateGiveaway}
              className="w-full py-3 rounded-xl bg-surface text-gray-200 font-semibold text-sm border border-white/10"
            >
              🎁 Create Giveaway
            </button>
          </>
        )}

        <button onClick={onClose} className="w-full py-2 text-gray-500 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

interface GiveawayModalProps {
  card: UserCard;
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

const DURATIONS = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
];

function GiveawayModal({ card, user, onClose, onSuccess }: GiveawayModalProps) {
  const [duration, setDuration] = useState(24);
  const [channelUsername, setChannelUsername] = useState('');
  const [requireChannel, setRequireChannel] = useState(false);
  const [requirePremium, setRequirePremium] = useState(false);
  const [requireOpenedPack, setRequireOpenedPack] = useState(false);
  const [requireTraded, setRequireTraded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tgUser = (window as Window & { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number; first_name?: string } } } } }).Telegram?.WebApp?.initDataUnsafe?.user;
  const tgId = String(tgUser?.id ?? user.tg_id ?? '0');
  const tgName = tgUser?.first_name ?? user.tg_name ?? 'User';

  async function handleSubmit() {
    setLoading(true);
    setError(null);
    try {
      await giveawaysApi.create({
        user_card_id: card.id,
        channel_username: requireChannel && channelUsername ? channelUsername.replace('@', '') : undefined,
        duration_hours: duration,
        requirements: {
          follow_slabvault: true,
          follow_channel: requireChannel,
          premium_only: requirePremium,
          opened_pack: requireOpenedPack,
          has_traded: requireTraded,
        },
        tg_id: tgId,
        tg_name: tgName,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create giveaway');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div
        className="w-full bg-surface-card rounded-t-2xl p-4 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />
        <h3 className="text-white font-bold text-lg text-center">Create Giveaway</h3>

        <div className="w-28 mx-auto">
          <SlabCard card={card} size="sm" />
        </div>
        <p className="text-center text-gray-400 text-sm">
          Prize: <span className="text-white font-semibold">{card.pokemon_name}</span>
          {' '}·{' '}<span className="text-accent-gold">${card.market_price_usd.toFixed(2)}</span>
        </p>

        <div>
          <p className="text-gray-400 text-sm mb-2">Duration</p>
          <div className="grid grid-cols-4 gap-2">
            {DURATIONS.map(({ label, hours }) => (
              <button
                key={hours}
                onClick={() => setDuration(hours)}
                className={`py-2 rounded-xl text-sm font-semibold border ${
                  duration === hours
                    ? 'bg-accent-purple border-accent-purple text-white'
                    : 'bg-surface border-white/10 text-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-gray-400 text-sm">Requirements</p>

          <div className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 border border-white/5">
            <span className="text-white text-sm">Follow @slabvault</span>
            <span className="text-green-400 text-xs font-medium">Always</span>
          </div>

          <div className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 border border-white/5">
            <span className="text-white text-sm">Follow your channel</span>
            <button
              onClick={() => setRequireChannel(!requireChannel)}
              className={`w-10 h-6 rounded-full transition-colors relative ${requireChannel ? 'bg-accent-purple' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${requireChannel ? 'left-5' : 'left-1'}`} />
            </button>
          </div>

          {requireChannel && (
            <input
              type="text"
              placeholder="@yourchannel"
              value={channelUsername}
              onChange={(e) => setChannelUsername(e.target.value)}
              className="w-full bg-surface text-white rounded-xl px-3 py-2.5 border border-white/10 outline-none focus:border-accent-purple text-base"
              style={{ fontSize: '16px' }}
            />
          )}

          <div className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 border border-white/5">
            <span className="text-white text-sm">Telegram Premium only</span>
            <button
              onClick={() => setRequirePremium(!requirePremium)}
              className={`w-10 h-6 rounded-full transition-colors relative ${requirePremium ? 'bg-accent-purple' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${requirePremium ? 'left-5' : 'left-1'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 border border-white/5">
            <div>
              <span className="text-white text-sm">Has opened a pack</span>
              <p className="text-gray-500 text-xs">Must have opened at least 1 pack</p>
            </div>
            <button
              onClick={() => setRequireOpenedPack(!requireOpenedPack)}
              className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${requireOpenedPack ? 'bg-accent-purple' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${requireOpenedPack ? 'left-5' : 'left-1'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between bg-surface rounded-xl px-3 py-2.5 border border-white/5">
            <div>
              <span className="text-white text-sm">Has traded on SlabVault</span>
              <p className="text-gray-500 text-xs">Must have bought or sold a card</p>
            </div>
            <button
              onClick={() => setRequireTraded(!requireTraded)}
              className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${requireTraded ? 'bg-accent-purple' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${requireTraded ? 'left-5' : 'left-1'}`} />
            </button>
          </div>
        </div>

        {error && <p className="text-red-400 text-sm text-center">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-accent-purple text-white font-bold disabled:opacity-50"
        >
          {loading ? 'Launching…' : '🚀 Launch Giveaway'}
        </button>
        <button onClick={onClose} className="w-full py-2 text-gray-500 text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function Collection({ user }: CollectionProps) {
  const [myCards, setMyCards] = useState<UserCard[]>([]);
  const [sort, setSort] = useState<SortOption>('newest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<UserCard | null>(null);
  const [showList, setShowList] = useState(false);
  const [showGiveaway, setShowGiveaway] = useState(false);

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cardsApi.my(sort);
      setMyCards(res.cards);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const totalValue = myCards.reduce((sum, c) => sum + c.market_price_usd, 0);

  function closeAll() {
    setSelectedCard(null);
    setShowList(false);
    setShowGiveaway(false);
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-white/5 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">My Collection</h2>
          <div className="text-right">
            <p className="text-accent-gold font-bold">${totalValue.toFixed(2)}</p>
            <p className="text-gray-500 text-xs">{myCards.length} cards</p>
          </div>
        </div>

        {/* Sort tabs */}
        <div className="flex gap-1 bg-surface rounded-xl p-1">
          {(['newest', 'rarest', 'value'] as SortOption[]).map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              className={`flex-1 py-1.5 text-xs rounded-lg font-medium capitalize transition-colors ${
                sort === s ? 'bg-accent-purple text-white' : 'text-gray-400'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 scroll-area p-3">
        {loading && (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-surface-card animate-pulse aspect-[3/5]" />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-10">
            <p className="text-red-400 text-sm">{error}</p>
            <button onClick={loadCards} className="text-accent-purple-light text-sm mt-2">Retry</button>
          </div>
        )}

        {!loading && !error && myCards.length === 0 && (
          <div className="text-center py-16 space-y-3">
            <p className="text-5xl">🃏</p>
            <p className="text-white font-semibold">No cards yet</p>
            <p className="text-gray-400 text-sm">Open a pack to start your collection!</p>
          </div>
        )}

        {!loading && !error && myCards.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {myCards.map((card) => (
              <div key={card.id} className="space-y-1" onClick={() => setSelectedCard(card)}>
                <SlabCard
                  card={card}
                  size="md"
                  onClick={() => setSelectedCard(card)}
                  badge={
                    card.listed ? (
                      <span className="bg-accent-gold/90 text-black text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase">
                        Listed
                      </span>
                    ) : undefined
                  }
                />
                <div className="px-1">
                  <p className="text-accent-gold font-bold text-xs">${card.market_price_usd.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedCard && !showList && !showGiveaway && (
        <ActionSheet
          card={selectedCard}
          onClose={closeAll}
          onListForSale={() => setShowList(true)}
          onCreateGiveaway={() => setShowGiveaway(true)}
        />
      )}

      {selectedCard && showList && (
        <ListModal
          card={selectedCard}
          onClose={closeAll}
          onSuccess={() => { closeAll(); loadCards(); }}
        />
      )}

      {selectedCard && showGiveaway && (
        <GiveawayModal
          card={selectedCard}
          user={user}
          onClose={closeAll}
          onSuccess={() => { closeAll(); loadCards(); }}
        />
      )}
    </div>
  );
}
